/**
 * A small markdown parser for the model's answer — a safe subset, no HTML.
 *
 * Blocks: paragraph (a single newline is a line break), heading (#…),
 * list (bullet or ordered, nested by indentation), codeblock (``` or ~~~),
 * table (GFM), hr, blockquote, math. Inline: text, br, strong, em, code, math.
 *
 * Links render as their text followed by the address in brackets, images as
 * their alt text only, raw HTML as literal text: nothing here can become a
 * link, an image or markup that fetches.
 *
 * Never throws: whatever it does not understand stays plain text.
 *
 * @param {string} text
 * @returns {Array<object>} blocks
 */
export function parseMarkdown(text) {
  if (typeof text !== 'string' || text === '') return [];
  const chars = Array.from(text);
  try {
    return parseBlocks(chars, splitLines(chars), 0, { cells: MAX_TABLE_CELLS });
  } catch {
    return [{ type: 'paragraph', children: [{ type: 'text', text }] }];
  }
}

// ── Lines ───────────────────────────────────────────────────────────────────

const MAX_DEPTH = 8;
const MAX_LINK_SCAN = 2000;
const MAX_EMPHASIS_DEPTH = 8;
// Table rows are padded to the header, so a few characters of `|` lines can
// ask for thousands of cells. The budget is for the whole answer; a 4096-token
// answer cannot carry more real cells than this. What does not fit is text.
const MAX_TABLE_COLUMNS = 50;
const MAX_TABLE_CELLS = 2000;

const BLANK = /^[ \t]*$/;
const FENCE = /^( {0,3})(`{3,}|~{3,})(.*)$/;
const HR = /^ {0,3}(?:(?:-[ \t]*){3,}|(?:\*[ \t]*){3,}|(?:_[ \t]*){3,})$/;
const HEADING = /^ {0,3}(#{1,6})(?=[ \t]|$)[ \t]*/;
const HEADING_CLOSE = /(?:^|[ \t]+)#+[ \t]*$/;
const QUOTE = /^ {0,3}>[ \t]?/;
const LIST = /^( {0,3})([-*+•]|(\d{1,9})[.)])([ \t]+|$)/;
const TABLE_SEP = /^[ \t]*\|?[ \t]*:?-+:?[ \t]*(?:\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/;

/**
 * A line is a code-point range `[s, e)` of the source, newline excluded, plus
 * its string. Container prefixes (`> `, list indentation) are stripped by
 * moving `s`, so a line stays one contiguous range of the source.
 */
function line(chars, s, e) {
  return { s, e, str: chars.slice(s, e).join('') };
}

function splitLines(chars) {
  const lines = [];
  let s = 0;
  for (let i = 0; i <= chars.length; i += 1) {
    if (i === chars.length || chars[i] === '\n') {
      const e = i > s && chars[i - 1] === '\r' ? i - 1 : i;
      lines.push(line(chars, s, e));
      s = i + 1;
    }
  }
  return lines;
}

function indentOf(str) {
  let col = 0;
  for (const ch of str) {
    if (ch === ' ') col += 1;
    else if (ch === '\t') col += 4 - (col % 4);
    else break;
  }
  return col;
}

/** Drop up to `cols` columns of leading whitespace. */
function stripIndent(chars, ln, cols) {
  let s = ln.s;
  let col = 0;
  while (s < ln.e && col < cols && (chars[s] === ' ' || chars[s] === '\t')) {
    col += chars[s] === '\t' ? 4 - (col % 4) : 1;
    s += 1;
  }
  return line(chars, s, ln.e);
}

const isBlank = (ln) => BLANK.test(ln.str);

function fenceOf(ln) {
  const m = FENCE.exec(ln.str);
  if (!m || (m[2][0] === '`' && m[3].includes('`'))) return null;
  return { indent: m[1].length, char: m[2][0], size: m[2].length, lang: m[3].trim() };
}

function isFenceClose(ln, fence) {
  const m = /^ {0,3}(`{3,}|~{3,})[ \t]*$/.exec(ln.str);
  return Boolean(m) && m[1][0] === fence.char && m[1].length >= fence.size;
}

/**
 * A list marker: `- `, `* `, `+ `, `• `, `1. `, `1) `. `contentLen` counts
 * code points to the item's text (every marker character is one code point).
 */
function listMarkerOf(ln) {
  if (HR.test(ln.str)) return null;
  const m = LIST.exec(ln.str);
  if (!m) return null;
  const markerEnd = m[1].length + m[2].length;
  const gap = m[4].length;
  const contentLen = gap === 0 || gap > 4 ? markerEnd + Math.min(gap, 1) : markerEnd + gap;
  return {
    ordered: m[3] !== undefined,
    number: m[3] !== undefined ? parseInt(m[3], 10) : null,
    contentLen,
    contentIndent: gap === 0 || gap > 4 ? markerEnd + 1 : markerEnd + gap,
    markerIndent: m[1].length,
    empty: BLANK.test(ln.str.slice(markerEnd)),
  };
}

/** Table cells of a row as trimmed code-point ranges; `\|` does not split. */
function splitCells(chars, ln) {
  let s = ln.s;
  let e = ln.e;
  while (s < e && isSpace(chars[s])) s += 1;
  while (e > s && isSpace(chars[e - 1])) e -= 1;
  if (chars[s] === '|') s += 1;
  if (e > s && chars[e - 1] === '|' && (e - 2 < s || chars[e - 2] !== '\\')) e -= 1;
  const cells = [];
  let start = s;
  const cps = chars.slice(s, e);
  const math = indexMath(cps);
  for (let k = s; k < e; k += 1) {
    const p = k - s;
    const pathEnd = pathAt(cps, p);
    const formula = pathEnd < 0 && (chars[k] === '\\' || chars[k] === '$') ? mathAt(cps, p, math, false) : null;
    if (pathEnd >= 0) k = s + pathEnd - 1;
    else if (formula && !formula.literal) k = s + formula.end - 1;
    else if (chars[k] === '\\') k += 1;
    else if (chars[k] === '$') k += runLength(cps, p, '$') - 1;
    else if (chars[k] === '|') {
      cells.push(trimRange(chars, start, k));
      start = k + 1;
    }
  }
  cells.push(trimRange(chars, start, e));
  return cells;
}

function trimRange(chars, s, e) {
  let a = s;
  let b = e;
  while (a < b && isSpace(chars[a])) a += 1;
  while (b > a && isSpace(chars[b - 1])) b -= 1;
  return { s: a, e: b };
}

const isSpace = (ch) => ch === ' ' || ch === '\t';

/** A table starts at `i` when the next line is a matching separator row. */
function tableAt(chars, lines, i) {
  const head = lines[i];
  const sep = lines[i + 1];
  if (!sep || !head.str.includes('|') || !sep.str.includes('|') || !TABLE_SEP.test(sep.str)) {
    return null;
  }
  const headCells = splitCells(chars, head);
  const sepCells = splitCells(chars, sep);
  if (headCells.length !== sepCells.length || headCells.length > MAX_TABLE_COLUMNS) return null;
  const align = sepCells.map(({ s, e }) => {
    const left = chars[s] === ':';
    const right = chars[e - 1] === ':';
    if (left && right) return 'center';
    if (right) return 'right';
    if (left) return 'left';
    return null;
  });
  return { headCells, align };
}

// ── Blocks ──────────────────────────────────────────────────────────────────

/** `ctx.cells` is the table-cell budget left for the whole answer. */
function parseBlocks(chars, lines, depth, ctx) {
  const blocks = [];
  let para = [];
  const flush = () => {
    if (para.length > 0) {
      const children = inlineOfLines(chars, para);
      if (children.length > 0) blocks.push({ type: 'paragraph', children });
      para = [];
    }
  };

  let i = 0;
  while (i < lines.length) {
    const ln = lines[i];
    if (isBlank(ln)) {
      flush();
      i += 1;
      continue;
    }
    if (depth >= MAX_DEPTH) {
      para.push(ln);
      i += 1;
      continue;
    }

    const fence = fenceOf(ln);
    if (fence) {
      flush();
      i = parseCodeBlock(chars, lines, i, fence, blocks);
      continue;
    }
    const display = displayOpenOf(ln);
    if (display) {
      flush();
      const parsed = parseMathBlock(chars, lines, i, display, blocks);
      i = parsed.end;
      if (parsed.tail) para.push(parsed.tail);
      continue;
    }
    if (HR.test(ln.str)) {
      flush();
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }
    const heading = HEADING.exec(ln.str);
    if (heading) {
      flush();
      const rest = ln.str.slice(heading[0].length);
      const close = HEADING_CLOSE.exec(rest);
      const tail = close ? close[0].length : rest.length - rest.trimEnd().length;
      const content = line(chars, ln.s + heading[0].length, ln.e - tail);
      const children = inlineOfLines(chars, [content]);
      if (children.length > 0) {
        blocks.push({ type: 'heading', level: Math.min(heading[1].length, 3), children });
      }
      i += 1;
      continue;
    }
    if (QUOTE.test(ln.str)) {
      flush();
      const inner = [];
      while (i < lines.length && QUOTE.test(lines[i].str)) {
        const prefix = QUOTE.exec(lines[i].str)[0].length;
        inner.push(line(chars, lines[i].s + prefix, lines[i].e));
        i += 1;
      }
      const quoted = parseBlocks(chars, inner, depth + 1, ctx);
      if (quoted.length > 0) blocks.push({ type: 'blockquote', blocks: quoted });
      continue;
    }
    const table = tableAt(chars, lines, i);
    if (table && table.headCells.length <= ctx.cells) {
      flush();
      i = parseTable(chars, lines, i, table, blocks, ctx);
      continue;
    }
    const marker = listMarkerOf(ln);
    if (marker && (para.length === 0 || (!marker.empty && (!marker.ordered || marker.number === 1)))) {
      flush();
      i = parseList(chars, lines, i, depth, blocks, ctx);
      continue;
    }
    para.push(ln);
    i += 1;
  }
  flush();
  return blocks;
}

function parseCodeBlock(chars, lines, start, fence, blocks) {
  let i = start + 1;
  const body = [];
  while (i < lines.length && !isFenceClose(lines[i], fence)) {
    body.push(stripIndent(chars, lines[i], fence.indent).str);
    i += 1;
  }
  if (/^(?:math|latex|tex)$/i.test(fence.lang)) {
    const source = lines.slice(start, i < lines.length ? i + 1 : i).map((ln) => ln.str).join('\n');
    if (i < lines.length && body.join('\n').trim() !== '') blocks.push({ type: 'math', display: true, text: body.join('\n'), source });
    else blocks.push({ type: 'paragraph', children: [{ type: 'text', text: source }] });
  } else blocks.push({ type: 'codeblock', lang: fence.lang, text: body.join('\n') });
  return i < lines.length ? i + 1 : i;
}

function displayOpenOf(ln) {
  const match = /^ {0,3}(\\\[|\$\$(?!\$))/.exec(ln.str);
  return match ? { close: match[1] === '$$' ? '$$' : '\\]', start: ln.s + match[0].length } : null;
}

/** A display consumes what it scans, even while streaming: no suffix rescans. */
function parseMathBlock(chars, lines, start, display, blocks) {
  const body = [];
  const source = [];
  for (let i = start; i < lines.length; i += 1) {
    const ln = lines[i];
    const from = i === start ? display.start : ln.s;
    let p = from;
    let close = -1;
    while (p < ln.e) {
      if (chars[p] === '\\') {
        if (display.close === '\\]' && chars[p + 1] === ']' && p + 1 < ln.e) { close = p; break; }
        p += p + 1 < ln.e && ASCII_PUNCT.has(chars[p + 1]) ? 2 : 1;
      } else if (chars[p] === '$') {
        let end = p + 1;
        while (end < ln.e && chars[end] === '$') end += 1;
        if (display.close === '$$' && end - p === 2) { close = p; break; }
        p = end;
      } else p += 1;
    }
    body.push(chars.slice(from, close >= 0 ? close : ln.e).join(''));
    source.push(chars.slice(ln.s, close >= 0 ? close + 2 : ln.e).join(''));
    if (close >= 0) {
      const text = body.join('\n');
      const raw = source.join('\n');
      blocks.push(text.trim() ? { type: 'math', text, display: true, source: raw } : { type: 'paragraph', children: [{ type: 'text', text: raw }] });
      const tail = line(chars, close + 2, ln.e);
      return { end: i + 1, tail: isBlank(tail) ? null : tail };
    }
  }
  blocks.push({ type: 'paragraph', children: [{ type: 'text', text: source.join('\n') }] });
  return { end: lines.length };
}

/** Rows past the cell budget are left to the caller, which shows them as text. */
function parseTable(chars, lines, start, table, blocks, ctx) {
  const width = table.headCells.length;
  const cellsOf = (ranges) => {
    ctx.cells -= width;
    return Array.from({ length: width }, (_, c) =>
      ranges[c] ? parseInline(chars.slice(ranges[c].s, ranges[c].e), 0) : [],
    );
  };
  const head = cellsOf(table.headCells);
  const rows = [];
  let i = start + 2;
  while (i < lines.length && !isBlank(lines[i]) && lines[i].str.includes('|') && ctx.cells >= width) {
    rows.push(cellsOf(splitCells(chars, lines[i])));
    i += 1;
  }
  blocks.push({ type: 'table', align: table.align, head, rows });
  return i;
}

/** Could this line open a block that ends a lazy list-item paragraph? */
function startsBlock(chars, lines, i) {
  const ln = lines[i];
  return Boolean(
    fenceOf(ln) ||
      displayOpenOf(ln) ||
      HR.test(ln.str) ||
      HEADING.test(ln.str) ||
      QUOTE.test(ln.str) ||
      listMarkerOf(ln) ||
      tableAt(chars, lines, i),
  );
}

function parseList(chars, lines, start, depth, blocks, ctx) {
  const ordered = listMarkerOf(lines[start]).ordered;
  const items = [];
  let i = start;
  let more = true;
  while (more && i < lines.length) {
    const marker = listMarkerOf(lines[i]);
    if (!marker || marker.ordered !== ordered) break;
    // A continuation line is one indented past the marker by two columns or
    // to the item's text, whichever is less: models nest `- ` under `1. `
    // with two spaces as often as with three.
    const need = Math.min(marker.contentIndent, marker.markerIndent + 2);
    const itemLines = [line(chars, lines[i].s + marker.contentLen, lines[i].e)];
    let prevBlank = false;
    i += 1;
    while (i < lines.length) {
      const ln = lines[i];
      if (isBlank(ln)) {
        let k = i;
        while (k < lines.length && isBlank(lines[k])) k += 1;
        if (k < lines.length && indentOf(lines[k].str) >= need) {
          for (; i < k; i += 1) itemLines.push(lines[i]);
          prevBlank = true;
          continue;
        }
        const next = k < lines.length ? listMarkerOf(lines[k]) : null;
        if (next && next.ordered === ordered) i = k;
        else more = false;
        break;
      }
      if (indentOf(ln.str) >= need) {
        itemLines.push(stripIndent(chars, ln, marker.contentIndent));
      } else if (!prevBlank && !startsBlock(chars, lines, i)) {
        itemLines.push(ln);
      } else {
        break;
      }
      prevBlank = false;
      i += 1;
    }
    items.push({ number: marker.number, blocks: parseBlocks(chars, itemLines, depth + 1, ctx) });
  }
  blocks.push({
    type: 'list',
    ordered,
    start: ordered && items.length > 0 ? items[0].number : null,
    items,
  });
  return i;
}

// ── Inline ──────────────────────────────────────────────────────────────────

const ASCII_PUNCT = new Set(Array.from('!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~'));
const PUNCT = /[\p{P}\p{S}]/u;
const WHITESPACE = /\s/u;
const DIGIT = /\p{Nd}/u;

/** Closing-token positions and monotone cursors make unsuccessful math linear. */
function indexMath(cps) {
  const positions = { paren: [], bracket: [], dollar: [], display: [], newline: [] };
  let p = 0;
  while (p < cps.length) {
    if (cps[p] === '\\' && p + 1 < cps.length && ASCII_PUNCT.has(cps[p + 1])) {
      if (cps[p + 1] === ')') positions.paren.push(p);
      if (cps[p + 1] === ']') positions.bracket.push(p);
      p += 2;
    } else if (cps[p] === '$') {
      const size = runLength(cps, p, '$');
      // Adjacent inline formulas share a two-dollar boundary: $x$$y$.
      if (size === 1 || size === 2) for (let k = 0; k < size; k++) positions.dollar.push(p + k);
      if (size === 2) positions.display.push(p);
      p += size;
    } else {
      if (cps[p] === '\n') positions.newline.push(p);
      p += 1;
    }
  }
  return { positions, cursors: {} };
}

function nextMathToken(index, kind, from) {
  const positions = index.positions[kind];
  let p = index.cursors[kind] || 0;
  while (p < positions.length && positions[p] < from) p += 1;
  index.cursors[kind] = p;
  return p < positions.length ? positions[p] : -1;
}

/** Dollar math uses the immediately next dollar, never jumps across a price. */
function dollarOpens(cps, p, close) {
  const after = cps[p + 1];
  if (!after || WHITESPACE.test(after)) return false;
  if (!DIGIT.test(after)) return true;
  let k = p + 1;
  while (k < cps.length && (DIGIT.test(cps[k]) || cps[k] === '.' || cps[k] === ',')) k += 1;
  while (k < cps.length && isSpace(cps[k])) k += 1;
  // $100$ and $2 + 2$ are formulas; $100 USD / $100 и $200 are money.
  return k === close || (k < cps.length && '+-*/=<>^_\\({'.includes(cps[k]));
}

function mathAt(cps, p, index, allowPartial = true) {
  let kind;
  let size;
  if (cps[p] === '\\' && ['(', '['].includes(cps[p + 1])) {
    kind = cps[p + 1] === '(' ? 'paren' : 'bracket';
    size = 2;
  } else if (cps[p] === '$') {
    size = runLength(cps, p, '$');
    if (size !== 1 && size !== 2) return null;
    kind = size === 1 ? 'dollar' : 'display';
  } else return null;
  const close = nextMathToken(index, kind, p + size);
  if (kind === 'dollar') {
    if (!dollarOpens(cps, p, close)) return null;
    const newline = nextMathToken(index, 'newline', p + size);
    const numericPrice = close >= 0 && /^[\p{Nd}.,]+$/u.test(cps.slice(p + size, close).join('')) && DIGIT.test(cps[close + 1] || '');
    if (close >= 0 && (WHITESPACE.test(cps[close - 1]) || numericPrice || (newline >= 0 && newline < close))) return null;
  }
  if (close < 0) return allowPartial ? { end: cps.length, literal: true, source: cps.slice(p).join('') } : null;
  const source = cps.slice(p, close + size).join('');
  const text = cps.slice(p + size, close).join('');
  return text.trim()
    ? { end: close + size, node: { type: 'math', text, display: kind === 'bracket' || kind === 'display', source } }
    : { end: close + size, literal: true, source };
}

/** Drive/UNC paths stay literal, including punctuation and math-looking parts. */
function windowsPathEnd(cps, p) {
  const drive = /^[a-z]$/i.test(cps[p] || '') && cps[p + 1] === ':' && cps[p + 2] === '\\';
  const unc = cps[p] === '\\' && cps[p + 1] === '\\' && /[\p{L}\p{N}]/u.test(cps[p + 2] || '');
  if ((!drive && !unc) || (p > 0 && /[\p{L}\p{N}_]/u.test(cps[p - 1]))) return -1;
  const quote = p > 0 && ['"', "'"].includes(cps[p - 1]) ? cps[p - 1] : null;
  let end = p;
  while (end < cps.length && cps[end] !== '\n' && (quote ? cps[end] !== quote : !WHITESPACE.test(cps[end]))) end += 1;
  return unc && !cps.slice(p + 2, end).includes('\\') ? -1 : end;
}

const pathAt = (cps, p) =>
  (cps[p] === '\\' && cps[p + 1] === '\\') || (cps[p + 1] === ':' && cps[p + 2] === '\\') ? windowsPathEnd(cps, p) : -1;

/** A paragraph's lines, trimmed and joined by line breaks, parsed inline. */
function inlineOfLines(chars, lines) {
  const cps = [];
  lines.forEach((ln, idx) => {
    const { s, e } = trimRange(chars, ln.s, ln.e);
    if (idx > 0) cps.push('\n');
    for (let k = s; k < e; k += 1) cps.push(chars[k]);
  });
  while (cps.length > 0 && cps[cps.length - 1] === '\n') cps.pop();
  return parseInline(cps, 0);
}

/**
 * Inline parse of code points `cps`.
 * Emphasis follows the CommonMark delimiter rules, so `snake_case` and
 * `2 * 3` stay text and an unclosed `**` stays literal.
 */
function parseInline(cps, depth) {
  const nodes = [];
  let text = null;
  const pushText = (str) => {
    if (str === '') return;
    if (!text) {
      text = { type: 'text', text: '' };
      nodes.push(text);
    }
    text.text += str;
  };
  const pushNode = (node) => {
    text = null;
    nodes.push(node);
  };

  const n = cps.length;
  const mathIndex = indexMath(cps);
  let p = 0;
  while (p < n) {
    const c = cps[p];
    const pathEnd = pathAt(cps, p);
    const formula = pathEnd < 0 && (c === '\\' || c === '$') ? mathAt(cps, p, mathIndex) : null;
    if (pathEnd >= 0) {
      pushText(cps.slice(p, pathEnd).join(''));
      p = pathEnd;
    } else if (formula) {
      if (formula.literal) pushText(formula.source);
      else pushNode(formula.node);
      p = formula.end;
    } else if (c === '$') {
      const size = runLength(cps, p, '$');
      pushText('$'.repeat(size));
      p += size;
    } else if (c === '\\' && p + 1 < n && ASCII_PUNCT.has(cps[p + 1])) {
      pushText(cps[p + 1]);
      p += 2;
    } else if (c === '\n') {
      pushNode({ type: 'br' });
      p += 1;
    } else if (c === '`') {
      const size = runLength(cps, p, '`');
      const close = findCodeClose(cps, p + size, size);
      if (close < 0) {
        pushText('`'.repeat(size));
      } else {
        pushNode(codeSpan(cps, p + size, close));
        p = close;
      }
      p += size;
    } else if (c === '!' && cps[p + 1] === '[' && depth < 3 && scanLink(cps, p + 1)) {
      // An image never loads: only its alt text is shown.
      const link = scanLink(cps, p + 1);
      pushText(cps.slice(p + 2, link.labelEnd).join(''));
      p = link.end;
    } else if (c === '[' && depth < 3 && scanLink(cps, p)) {
      // A link is never a link: its text, then its address as plain text.
      const link = scanLink(cps, p);
      parseInline(cps.slice(p + 1, link.labelEnd), depth + 1).forEach(pushNode);
      const label = cps.slice(p + 1, link.labelEnd).join('').trim();
      const dest = trimRange(cps, link.destStart, link.destEnd);
      const address = cps.slice(dest.s, dest.e).join('');
      if (address !== '' && address !== label) pushText(` (${address})`);
      p = link.end;
    } else if (c === '*' || c === '_') {
      const size = runLength(cps, p, c);
      pushNode(delimiter(cps, p, size));
      p += size;
    } else {
      pushText(c);
      p += 1;
    }
  }
  return resolveEmphasis(nodes);
}

function runLength(cps, p, ch) {
  let k = p;
  while (k < cps.length && cps[k] === ch) k += 1;
  return k - p;
}

/** Start of the closing backtick run of exactly `size`, or -1. */
function findCodeClose(cps, from, size) {
  let k = from;
  while (k < cps.length) {
    if (cps[k] === '`') {
      const run = runLength(cps, k, '`');
      if (run === size) return k;
      k += run;
    } else {
      k += 1;
    }
  }
  return -1;
}

function codeSpan(cps, s, e) {
  const body = cps.slice(s, e).map((ch) => (ch === '\n' ? ' ' : ch));
  const padded =
    body.length > 1 && body[0] === ' ' && body[body.length - 1] === ' ' && body.some((ch) => ch !== ' ');
  return { type: 'code', text: (padded ? body.slice(1, -1) : body).join('') };
}

/** `[label](address)` starting at `p` (a `[`), or null. */
function scanLink(cps, p) {
  const limit = Math.min(cps.length, p + MAX_LINK_SCAN);
  let depth = 0;
  let k = p;
  for (; k < limit; k += 1) {
    if (cps[k] === '\\') k += 1;
    else if (cps[k] === '[') depth += 1;
    else if (cps[k] === ']') {
      depth -= 1;
      if (depth === 0) break;
    }
  }
  if (k >= limit || cps[k + 1] !== '(') return null;
  const labelEnd = k;
  let parens = 1;
  let q = k + 2;
  for (; q < limit; q += 1) {
    if (cps[q] === '\\') q += 1;
    else if (cps[q] === '\n') return null;
    else if (cps[q] === '(') parens += 1;
    else if (cps[q] === ')') {
      parens -= 1;
      if (parens === 0) break;
    }
  }
  if (q >= limit) return null;
  return { labelEnd, destStart: k + 2, destEnd: q, end: q + 1 };
}

function delimiter(cps, p, size) {
  const ch = cps[p];
  const before = p > 0 ? cps[p - 1] : ' ';
  const after = p + size < cps.length ? cps[p + size] : ' ';
  const spaceBefore = WHITESPACE.test(before);
  const spaceAfter = WHITESPACE.test(after);
  const punctBefore = PUNCT.test(before);
  const punctAfter = PUNCT.test(after);
  const left = !spaceAfter && (!punctAfter || spaceBefore || punctBefore);
  const right = !spaceBefore && (!punctBefore || spaceAfter || punctAfter);
  // `*` between digits is multiplication (1348*100/4000): emphasis there
  // would hide the operator and run two numbers together.
  const product = ch === '*' && DIGIT.test(before) && DIGIT.test(after);
  return {
    type: 'delim',
    char: ch,
    count: size,
    orig: size,
    // `_` inside a word is never emphasis: snake_case stays as written.
    canOpen: !product && (ch === '*' ? left : left && (!right || punctBefore)),
    canClose: !product && (ch === '*' ? right : right && (!left || punctAfter)),
  };
}

/**
 * CommonMark's "process emphasis" over a flat node list. Nesting stops at
 * MAX_EMPHASIS_DEPTH (a run of `*` can otherwise nest thousands deep and
 * overflow the stack when rendered): deeper delimiters stay literal text.
 */
function resolveEmphasis(nodes) {
  const bottom = {};
  // No opener at or below this index may be used: it would enclose a node
  // already at the nesting cap.
  let capFloor = -1;
  let i = 0;
  while (i < nodes.length) {
    const closer = nodes[i];
    if (closer.type !== 'delim' || !closer.canClose || closer.count === 0) {
      i += 1;
      continue;
    }
    const key = `${closer.char}${closer.canOpen ? 1 : 0}${closer.orig % 3}`;
    const floor = Math.max(key in bottom ? bottom[key] : -1, capFloor);
    let found = -1;
    for (let j = i - 1; j > floor; j -= 1) {
      const opener = nodes[j];
      if (opener.type !== 'delim' || opener.char !== closer.char || !opener.canOpen || opener.count === 0) {
        continue;
      }
      const both = opener.canClose || closer.canOpen;
      if (both && (opener.orig + closer.orig) % 3 === 0 && !(opener.orig % 3 === 0 && closer.orig % 3 === 0)) {
        continue;
      }
      found = j;
      break;
    }
    if (found < 0) {
      bottom[key] = i - 1;
      i += 1;
      continue;
    }

    let level = 1;
    for (let k = found + 1; k < i; k += 1) {
      if (nodes[k].level >= level) level = nodes[k].level + 1;
    }
    if (level > MAX_EMPHASIS_DEPTH) {
      capFloor = found;
      continue;
    }

    const opener = nodes[found];
    const use = opener.count >= 2 && closer.count >= 2 ? 2 : 1;
    const children = finishInline(nodes.slice(found + 1, i));
    opener.count -= use;
    closer.count -= use;
    nodes.splice(found + 1, i - found - 1, { type: use === 2 ? 'strong' : 'em', level, children });
    Object.keys(bottom).forEach((k) => {
      if (bottom[k] > found) bottom[k] = found;
    });
    i = found + 2;
  }
  return finishInline(nodes);
}

/**
 * Unmatched delimiters become text; adjacent text nodes merge. The merged node
 * is a fresh one grown in place, so a long run of merges stays linear.
 */
function finishInline(nodes) {
  const out = [];
  let open = null;
  for (const node of nodes) {
    let text;
    if (node.type === 'delim') {
      if (node.count === 0) continue;
      text = node.char.repeat(node.count);
    } else if (node.type === 'text') {
      ({ text } = node);
    } else {
      out.push(node);
      open = null;
      continue;
    }
    if (open) {
      open.text += text;
    } else {
      open = { type: 'text', text };
      out.push(open);
    }
  }
  return out;
}
